import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import ISite from '../../../_interfaces/ISite';
import { CustomersService } from '../../../_services/customers/customers.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { SessionService } from '../../../_services/session/session.service';
import { SitesService } from '../../../_services/sites/sites.service';
import { BaseComponent } from '../../../components/base/base.component';
import { AdminStatePanelComponent } from '../../admin-ui/admin-state-panel/admin-state-panel.component';
import { AdminTableActionsComponent } from '../../admin-ui/admin-table-actions/admin-table-actions.component';
import { AdminListToolbarComponent } from '../../admin-ui/admin-list-toolbar/admin-list-toolbar.component';

@Component({
  standalone: true,
  selector: 'app-table-sites',
  templateUrl: './table-sites.component.html',
  styleUrls: ['./table-sites.component.scss'],
  imports: [
    CommonModule,
    TableModule,
    TranslatePipe,
    ButtonModule,
    AdminTableActionsComponent,
    AdminStatePanelComponent,
    AdminListToolbarComponent,
  ],
})
export class TableSitesComponent extends BaseComponent implements OnInit {
  public sites: ISite[] = [];
  public filteredSites: ISite[] = [];
  public searchValue = '';

  get showCurrencyColumn(): boolean {
    return (this.sites || []).length > 1;
  }

  constructor(
    public readonly siteService: SitesService,
    public readonly confirmationService: ConfirmationService,
    public readonly messageService: MessageService,
    public readonly errorsService: ErrorsService,
    public readonly customersService: CustomersService,
    public readonly session: SessionService,
    public readonly translate: TranslateService,
    public readonly router: Router,
  ) {
    super(session);
  }

  onSearch(value: string): void {
    this.searchValue = value;
    const query = value.trim().toLowerCase();

    if (!query) {
      this.filteredSites = [...this.sites];
      return;
    }

    this.filteredSites = this.sites.filter((site) =>
      [site.name, site.address, site.email, site.phone]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }

  load(): void {
    this.customersService.getSites(this.customers_id).subscribe({
      next: (sites: ISite[]) => {
        this.sites = sites;
        this.onSearch(this.searchValue);
      },
      error: (err: HttpErrorResponse) => {
        const code = this.errorsService.getMessageFromSiteError(err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('MESSAGES.SITES.ERROR.SUMMARY'),
          detail: this.translate.instant(`MESSAGES.SITES.ERROR.${code}`),
        });
      },
    });
  }

  confirmDelete(site: ISite): void {
    if (!site.id) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('ADMIN.SITES.CONFIRM.DELETE.TITLE', {
        site: site.name,
      }),
      message: this.translate.instant('ADMIN.SITES.CONFIRM.DELETE.TEXT', {
        site: site.name,
      }),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(site),
    });
  }

  private delete(site: ISite): void {
    if (!site.id) {
      return;
    }

    this.siteService.delete(this.customers_id, site.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('MESSAGES.SITES.DELETE.SUMMARY'),
          detail: this.translate.instant('MESSAGES.SITES.DELETE.DETAIL', {
            site: site.name,
          }),
        });
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        const code = this.errorsService.getMessageFromSiteError(err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('MESSAGES.SITES.ERROR.SUMMARY'),
          detail: this.translate.instant(`MESSAGES.SITES.ERROR.${code}`),
        });
      },
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.load();
  }
}
